import { Overlay } from '@angular/cdk/overlay';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import tokml from 'geojson-to-kml';
import { Observable } from 'rxjs';

import { SiteService } from '../../../core/api/site.service';
import { buildAddressLine1, buildCityStateLine } from '../../../core/helper/addresses';
import { MapService } from '../../../core/helper/map.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { DialogImportKmlComponent } from '../../../shared/dialogs/dialog-import-kml/dialog-import-kml.component';
import { Parcel, Site, Stand, User } from '../../../shared/models';
import * as fromApp from '../../../store';
import { selectIsMobileView } from '../../../store/app-config/app-config.selectors';
import {
  setMapControlDrawMode,
  setMapControlsForestOff,
  toggleDrawPolygonMode,
  toggleMapControlsBaseMap,
  toggleMapControlsEditMode,
  toggleMapControlsForest,
  toggleMapControlsKMLUpload,
  toggleMapControlsPublicLand,
  toggleMapControlsRoads,
  toggleMapControlsStands,
  toggleMapControlsStandSelector,
  toggleMapControlsSuggestedStandsSelector,
  toggleMapControlsWaterways,
} from '../../../store/map-control/map-control.actions';
import { MapControlState, MapDisplayTypeEnum } from '../../../store/map-control/map-control.reducer';
import { selectMapControlState } from '../../../store/map-control/map-control.selectors';

@Component({
  selector: 'fc-map-control',
  templateUrl: './map-control.component.html',
  styleUrls: ['./map-control.component.scss'],
})
export class MapControlComponent implements OnInit, OnChanges {
  @Input() drawer: MatDrawer;
  @Input() currentUser: User = null;
  @Input() readOnlyMap: boolean = null;
  @Input() activeSite: Site = null;
  @Input() showParcelTip: boolean = false;
  @Input() currentStands: Stand[] = [];
  @Input() approvalRequired: boolean = false;

  @Output() generateMap: EventEmitter<any> = new EventEmitter();

  destroyRef = inject(DestroyRef);

  readonly buildAddressLine1 = buildAddressLine1;
  readonly buildCityStateLine = buildCityStateLine;
  mapControls$: Observable<MapControlState>;
  selectedParcels: Parcel[];

  isMobile = false;

  showInfoToolTip = true;
  showMapToolHelp = false;
  showParcels = false;
  hideParcelTip = false;
  siteId: number;
  hideInfoTooltip = true;

  isAdmin: boolean = false;
  showLayersOverlay = false;
  showMapToolsOverlay = false;

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly siteService: SiteService,
    readonly siteHelper: SiteHelperService,
    readonly mapService: MapService,
    readonly router: Router,
    readonly route: ActivatedRoute,
    public overlay: Overlay,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog
  ) {
    this.mapControls$ = store.select(selectMapControlState);
    this.siteId = +this.route.snapshot.params.id;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.currentUser?.currentValue?.isAdmin) {
      this.store.dispatch(toggleMapControlsPublicLand());
    }
    if (changes.activeSite?.currentValue) {
      this.selectedParcels = this.activeSite.versions[0].parcels;
      this.hideInfoTooltip = changes.activeSite.currentValue.versions.slice(-1)[0].stands.length > 0;
    }
    if (changes.readonlyMap?.currentValue) {
      this.store.dispatch(setMapControlDrawMode({ active: false }));
    }
  }

  ngOnInit() {
    this.store
      .select(selectIsMobileView)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mobileViewMode => {
        this.isMobile = mobileViewMode;
        if (!this.isMobile) {
          this.showLayersOverlay = false;
          this.showMapToolsOverlay = false;
        }
      });
  }

  ngOnDestroy() {
    this.store.dispatch(setMapControlsForestOff());

    this.showLayersOverlay = false;
    this.showMapToolsOverlay = false;
  }

  toggleMapControlsEvent(modeType: string) {
    switch (modeType) {
      case 'standSelector':
        this.store.dispatch(toggleMapControlsStandSelector());
        break;
      case 'editMode':
        this.store.dispatch(toggleMapControlsEditMode());
        break;
      case 'drawPolygonMode':
        this.store.dispatch(toggleDrawPolygonMode());
        break;
      case 'displayForest':
        this.store.dispatch(toggleMapControlsForest());
        break;
      case 'suggestedStandsSelector':
        this.store.dispatch(toggleMapControlsSuggestedStandsSelector());
        break;
      case 'displayWaterways':
        this.store.dispatch(toggleMapControlsWaterways());
        break;
      case 'displayStands':
        this.store.dispatch(toggleMapControlsStands());
        break;
      case 'displayRoads':
        this.store.dispatch(toggleMapControlsRoads());
        break;
      case 'displayContour':
        this.store.dispatch(toggleMapControlsBaseMap());
        break;
      case 'displayPublicLand':
        this.store.dispatch(toggleMapControlsPublicLand());
        break;
      case 'displayKMLUpload':
        this.store.dispatch(toggleMapControlsKMLUpload());
        break;
      default:
        throw new Error('invalid modeType');
    }
  }

  downloadKML() {
    const geoJSONFile = {
      type: 'FeatureCollection',
      features: this.currentStands.map(stand => stand.feature),
    };
    const kml = tokml(geoJSONFile);

    // Create a Blob from the KML data
    let blob = new Blob([kml.toString()], {
      type: 'application/vnd.google-earth.kml+xml',
    });

    // Create a download link
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `Site_${this.siteId}_definition.kml`;

    // Append the link to the DOM (optional, you can also trigger a click on the link)
    document.body.appendChild(link);

    // Trigger a click on the link to start the download
    link.click();

    // Remove the link from the DOM after the download is initiated (optional)
    document.body.removeChild(link);
  }

  showParcelHelp() {
    this.showParcels = !this.showParcels;
  }
  handleKMLClicked() {
    this.dialog.open(DialogImportKmlComponent, {
      data: {
        site: this.activeSite,
      },
    });
  }

  closeInfoTooltip() {
    this.hideInfoTooltip = true;
    this.showInfoToolTip = false;
  }

  closeParcelTooltip() {
    this.hideParcelTip = true;
    this.showParcelTip = false;
  }

  toggleMapToolHelp() {
    this.showMapToolHelp = !this.showMapToolHelp;
  }

  handleEditParcelsClick(): void {
    let url = `/site/${this.siteId}/locate-property/select-parcels`;

    if (this.currentUser && (this.currentUser.isAdmin || this.currentUser.isLimitedAdmin)) {
      url = `/admin/${this.siteId}/locate-property/select-parcels`;
    }
    this.router.navigate([url]);
  }

  handleSiteDetailsClick(): void {
    let url = `/account/site/${this.siteId}`;

    if (this.currentUser && (this.currentUser.isAdmin || this.currentUser.isLimitedAdmin)) {
      url = `/admin/site/${this.siteId}/`;
    }
    this.router.navigate([url]);
  }

  protected readonly MapDisplayTypeEnum = MapDisplayTypeEnum;
}
